export interface ModuleDeliverable {
  title: string;
}

export interface Module {
  id: number;
  title: string;
  description?: string;
  fullDescription?: string;
  transformation?: string;
  skills?: string[];
  deliverables?: ModuleDeliverable[];
  tech?: string[];
}

export interface Academy {
  id: string;
  name: string;
  tagline?: string;
  description: string;
  modules: Module[];
  comingSoon?: boolean;
}

export const academiesData: Academy[] = [
  {
    id: "faceless-content",
    name: "Faceless Content Systems",
    tagline: "The Invisible Virality Machine",
    description: "How to create viral, automated, and profitable content without showing your face.",
    modules: [
      {
        id: 1,
        title: "The Invisible Virality Code",
        transformation: "Teach students to understand how algorithms and virality work without ever appearing on video.",
        fullDescription: "Students learn how the viral cycle works: retention, watch time, CTR, repetition, sound design, and short-form storytelling adapted to faceless formats. Includes analysis of million-follower faceless accounts and reverse engineering.",
        skills: [
          "Understand viral triggers",
          "Understand anonymous human behavior",
          "Create invisible attention hooks"
        ],
        deliverables: [
          { title: "Guide: The 32 Faceless Virality Triggers" },
          { title: "10 Ready-to-Use Invisible Viral Video Scripts" }
        ],
        tech: ["ChatGPT", "OpusClip", "CapCut", "Pika Labs", "Runway"]
      },
      {
        id: 2,
        title: "Faceless Niche Architecture",
        transformation: "Choose viral and scalable niches that work without showing your face.",
        fullDescription: "Framework for niche analysis using AI + trends. Includes faceless micro-niches (list of 250).",
        skills: [
          "Professional niche selection",
          "CPM, demand, and competition analysis",
          "Invisible persona creation"
        ],
        deliverables: [
          { title: "Database of 250 Faceless Niches" },
          { title: "Ideal Niche Tool (Interactive Prompt)" }
        ],
        tech: ["ChatGPT", "Google Trends", "VidIQ"]
      },
      {
        id: 3,
        title: "Automated Scripts & Storytelling",
        transformation: "Create complete texts and scripts effortlessly using only AI.",
        fullDescription: "How to use AI to create 15-60s storytelling that generates insane retention. F.A.C.E. Framework = Faceless Attention Capture Engine.",
        skills: [
          "Short script construction",
          "Viral writing",
          "Ghost storytelling"
        ],
        deliverables: [
          { title: "50 Ready-to-Use Faceless Scripts" },
          { title: "AI Script Templates" },
          { title: "F.A.C.E. Framework" }
        ],
        tech: ["ChatGPT", "Claude", "Notion AI"]
      },
      {
        id: 4,
        title: "100% AI Video Production",
        transformation: "Create complete videos without filming, recording, or using your voice.",
        fullDescription: "AI for videos: avatars, animations, automatic b-roll, AI voice, automatic music.",
        skills: [
          "AI video production",
          "Quick editing for retention",
          "Faceless viral captions"
        ],
        deliverables: [
          { title: "Blueprint: Complete Video in 10 Minutes" },
          { title: "Premium Faceless Assets Pack" }
        ],
        tech: ["Runway", "Pika Labs", "ElevenLabs", "CapCut", "Pictory"]
      },
      {
        id: 5,
        title: "Faceless TikTok Mastery",
        transformation: "Master TikTok with an invisible identity.",
        fullDescription: "Virality strategies, frequency, invisible hashtags, shadow tactics, repost automation.",
        skills: [
          "TikTok Growth",
          "Anonymous account building",
          "Repeatable virality"
        ],
        deliverables: [
          { title: "30-Day Editorial Calendar" },
          { title: "20 Faceless Viral Ideas" },
          { title: "Upload Automation Script" }
        ],
        tech: ["TikTok", "CapCut", "Later", "Repurpose.io"]
      },
      {
        id: 6,
        title: "High-Retention Reels & Shorts",
        transformation: "Strategies to dominate Reels and Shorts without appearing.",
        fullDescription: "How to adapt TikTok content for Reels/Shorts with platform-specific retention adjustments.",
        skills: [
          "Multi-platform distribution",
          "Advanced retention",
          "Platform optimization"
        ],
        deliverables: [
          { title: "10/10 Retention Checklist" },
          { title: "15 Faceless Thumbnail Templates" }
        ],
        tech: ["Instagram", "YouTube", "CapCut", "Canva"]
      },
      {
        id: 7,
        title: "YouTube Phantom Automation",
        transformation: "Create entire channels without voice, face, or recording.",
        fullDescription: "AI for voice, video, editing, and automatic channel structure. Includes case studies of channels making $50k-$100k/month without appearing.",
        skills: [
          "Operate AI channel",
          "Automate uploads",
          "Create high-CTR thumbnails"
        ],
        deliverables: [
          { title: "AI Channel in 48h System" },
          { title: "10 Ready Scripts (MrBeast Faceless Style)" }
        ],
        tech: ["YouTube Studio", "ElevenLabs", "Pictory", "TubeBuddy"]
      },
      {
        id: 8,
        title: "SEO for Faceless Content",
        transformation: "Make videos grow organically via search and trends.",
        fullDescription: "Invisible optimization, keyword clustering, retention and recommendation strategies.",
        skills: [
          "Modern SEO",
          "Organic growth",
          "Data analysis"
        ],
        deliverables: [
          { title: "Ghost SEO Guide" },
          { title: "AI Tracking Spreadsheet" }
        ],
        tech: ["VidIQ", "TubeBuddy", "Ahrefs", "Google Trends"]
      },
      {
        id: 9,
        title: "Invisible Express Monetization",
        transformation: "Transform any faceless account into an income source.",
        fullDescription: "Invisible affiliates, sponsored content without appearing, hidden links, faceless digital products.",
        skills: [
          "Monetization",
          "Asset creation",
          "Income optimization"
        ],
        deliverables: [
          { title: "50 Faceless Affiliate Offers" },
          { title: "Invisible Sales Page Template" }
        ],
        tech: ["ClickBank", "Digistore24", "Gumroad", "Stan Store"]
      },
      {
        id: 10,
        title: "Multi-Channel Scale System",
        transformation: "Scale to 3-10 faceless accounts with total automation.",
        fullDescription: "How to create an entire network of invisible accounts operating on autopilot.",
        skills: [
          "Total automation",
          "Network management",
          "Invisible delegation"
        ],
        deliverables: [
          { title: "Complete Scale SOP" },
          { title: "Multi-Account Automation Script" }
        ],
        tech: ["Zapier", "Make", "Notion", "Slack"]
      }
    ],
  },
  {
    id: "claude-bot-employee",
    name: "Claude BOT Employee",
    tagline: "Coming Soon",
    description: "Build your first AI employee with Claude — automate tasks, customer support, content workflows, and internal operations. Launching soon.",
    comingSoon: true,
    modules: [],
  },
];

export const getAcademyById = (id: string): Academy | undefined => {
  return academiesData.find((academy) => academy.id === id);
};
